import { useMemo, useState } from 'react';
import { useCanSeeWarehouses, usePermission } from '../../store/auth';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useUrlString } from '../../hooks/useUrlState';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { Card, Select, Input, Typography, Row, Col, Tag, Progress, Segmented, Space, Table, theme, EmptyState, Button, StatCard } from '../../ui';
import { Search, Download, IndianRupee, Tags, TrendingUp, Boxes, Layers } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Cell, LabelList, PieChart, Pie, Tooltip as RTooltip } from 'recharts';
import { accentForProductName } from '../../lib/productAccent';
import { productGroupKey, productGroupName, disambiguateGroupNames } from '../../lib/productGroup';
import { warehouseOption } from '../../lib/warehouseLabel';
import { request } from '../../api/client';
import { ExportDialog } from '../../components/ExportDialog';
import { TwDataTable as DataTable, type DataTableColumn } from '../../components/TwDataTable';
import { fmtQty, fmtPackQty, isRedundantSku, inrShort } from '../../lib/format';
import { brand } from '../../assets/brand';
import { inr2 } from '../../lib/format';
import { PALETTE, seriesColorsFor } from '../../lib/palette';

interface BatchRow { batchNo: string; mfgDate: string | null; expDate: string | null; qty: number; unitCost: number; costValue: number; }
interface ProductRow {
  productId: string; product: string; sku: string | null; groupCode?: string | null; variantLabel?: string | null; packSize?: number; uom?: string | null;
  qty: number; batchCount: number; warehouseCount: number; costValue: number; retailValue: number; avgUnitCost: number; marginPct: number; sharePct: number;
}
interface ItemRow {
  warehouseId: string; warehouse: string; productId: string; product: string; sku: string | null;
  packSize?: number; uom?: string | null; mrp: number; qty: number; batchCount: number; costValue: number; retailValue: number;
  value: number; avgUnitCost: number; marginPct: number; sharePct: number; batches: BatchRow[];
}
interface WhRow { warehouseId: string; warehouse: string; qty: number; productCount: number; costValue: number; retailValue: number; sharePct: number; }
interface Summary {
  totalCostValue: number; totalRetailValue: number; totalMargin: number; marginPct: number; totalUnits: number;
  distinctProducts: number; distinctBatches: number; distinctWarehouses: number;
  topProduct: { product: string; costValue: number; sharePct: number } | null;
}
interface ValuationResp { items: ItemRow[]; byProduct: ProductRow[]; byWarehouse: WhRow[]; summary: Summary; totalValue: number; }

const inr = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const d10 = (v: string | null) => (v ? String(v).slice(0, 10) : '—');
const marginColor = (m: number) => (m >= 50 ? PALETTE.success[600] : m >= 20 ? PALETTE.warning[600] : PALETTE.danger[600]);

/** How many columns the card can LABEL legibly — a chart that cannot label a bar is not showing it.
 *  Seventeen columns read cleanly across a desktop card and pile into an unreadable smear on a
 *  phone, so the tail folds into a single "Others" column sooner on a narrow screen. */
const MAX_BARS_WIDE = 20;
const MAX_BARS_NARROW = 6;

/** Angled, truncated x tick. A product name is far wider than its column, so it is rotated and cut
 *  with an ellipsis; the full name is one hover away in the tooltip. */
function ProductTick({ x, y, payload, cut = 14 }: any) {
  const full = String(payload?.value ?? '');
  const text = full.length > cut ? `${full.slice(0, cut - 1)}…` : full;
  return (
    <g transform={`translate(${x},${y + 8})`}>
      <text transform="rotate(-38)" textAnchor="end" fontSize={11} fill="var(--gb-text-muted)">{text}</text>
    </g>
  );
}

/** Warehouse ring tooltip — the full site name the legend abbreviates to its code. */
function WarehouseTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const w = payload[0]?.payload;
  if (!w) return null;
  return (
    <div className="rounded-control border border-line bg-surface px-3 py-2 shadow-pop">
      <p className="type-label text-content">{w.warehouse}</p>
      <p className="type-body-strong tabular-nums text-content">{inr(w.costValue)}</p>
      <p className="type-caption text-muted">{w.sharePct}% of stock value · {w.productCount} products</p>
    </div>
  );
}

/** Tooltip in the app's surface/line/shadow language, not Recharts' default white box. */
function ValueTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <div className="rounded-control border border-line bg-surface px-3 py-2 shadow-pop">
      <p className="type-label text-content">{row.product}</p>
      <p className="type-body-strong tabular-nums text-content">{inr(row.costValue)}</p>
      <p className="type-caption text-muted">
        {row.sharePct}% of stock value
        {row.skuCount > 1 ? ` · ${row.skuCount} pack sizes` : ''}
        {row.qty ? ` · ${fmtQty(row.qty)} units` : ''}
      </p>
    </div>
  );
}

export function StockValuationPage({ embedded, summaryOnly }: { embedded?: boolean; summaryOnly?: boolean } = {}) {
  const canSeeWarehouses = useCanSeeWarehouses();
  const { token } = theme.useToken();
  const [warehouseId, setWarehouseId] = useUrlString<string | undefined>('wh', undefined);
  const [search, setSearch] = useUrlString('q');
  const [view, setView] = useUrlString<'product' | 'detail'>('view', 'product');
  // Gated on the same permission as the JSON endpoint: the sheet is priced at DEALER cost, and
  // STOCK.VALUATION.VIEW is the line the app already draws around who may see that.
  const canExport = usePermission('STOCK.VALUATION.VIEW');
  // The product card is full-width on a phone and 15/24 of the row on a desktop; below this the
  // columns are too narrow to carry a legible name.
  const narrow = useMediaQuery('(max-width: 900px)');
  const maxBars = narrow ? MAX_BARS_NARROW : MAX_BARS_WIDE;
  const [exportOpen, setExportOpen] = useState(false);

  const { data: warehouses } = useQuery({ queryKey: ['wh-list'], queryFn: () => request<any[]>({ url: '/warehouses/my' }), enabled: canSeeWarehouses });

  const params = useMemo(() => {
    const p: Record<string, string> = {};
    if (warehouseId) p.warehouseId = warehouseId;
    if (search.trim()) p.search = search.trim();
    return p;
  }, [warehouseId, search]);

  const { data, isFetching } = useQuery({
    queryKey: ['stock-valuation', params],
    queryFn: () => request<ValuationResp>({ url: '/stock/valuation', params }),
    placeholderData: keepPreviousData,
  });

  const s = data?.summary;
  const byProduct = data?.byProduct ?? [];
  const items = data?.items ?? [];
  const byWarehouse = data?.byWarehouse ?? [];
  const empty = !isFetching && (s?.totalCostValue ?? 0) === 0;
  // The "by warehouse" donut is meaningless with a single warehouse (always 100%) — e.g. a Warehouse
  // Manager scoped to one. In that case show the value split by product instead.
  const showWarehouseDonut = byWarehouse.length > 1;
  // Batch numbers per product (gathered across warehouses) so each row can list them inline.
  const batchNosByProduct = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const it of items) {
      const arr = m.get(it.productId) ?? [];
      for (const b of it.batches ?? []) if (b.batchNo && !arr.includes(b.batchNo)) arr.push(b.batchNo);
      m.set(it.productId, arr);
    }
    return m;
  }, [items]);

  /* `byProduct` is one row per SKU, because a row in `products` IS a pack size. The stock list
     underneath this panel lists PRODUCTS (AminOPF, its three packs folded into one row), so the
     chart has to roll up the same way or the panel contradicts the list it introduces: AminOPF
     showed as a 13.2L bar (its 1 L pack alone) above a list row reading 24.4L, "C4L Biofoliar"
     appeared twice, and a product could be missing from the chart while sitting at the top of the
     list. Family key + name come from lib/productGroup — the same helpers buildGroups() uses, and
     `byProduct` arrives value-sorted, so a family is named after its largest variant, not after
     whichever row happened to come first. */
  const byGroup = useMemo(() => {
    const total = data?.summary?.totalCostValue ?? 0;
    const m = new Map<string, { key: string; product: string; qty: number; skuCount: number; costValue: number; retailValue: number; sharePct: number }>();
    for (const p of byProduct) {
      const key = productGroupKey(p);
      let g = m.get(key);
      if (!g) { g = { key, product: productGroupName(p) || p.product, qty: 0, skuCount: 0, costValue: 0, retailValue: 0, sharePct: 0 }; m.set(key, g); }
      g.qty += p.qty; g.skuCount += 1; g.costValue += p.costValue; g.retailValue += p.retailValue;
    }
    return disambiguateGroupNames(
      [...m.values()]
        // A value chart can say nothing about a product priced at zero, and eight empty rows only
        // make the products that DO carry value harder to find. They still count in the KPI band.
        .filter((g) => g.costValue > 0)
        .map((g) => ({ ...g, costValue: Number(g.costValue.toFixed(2)), sharePct: total > 0 ? Number(((g.costValue / total) * 100).toFixed(2)) : 0 }))
        .sort((a, b) => b.costValue - a.costValue),
    );
  }, [byProduct, data]);
  /* ONE fixed height for both breakdown cards. A ranked bar list grows a row per product, so it
     either scrolls inside the card or pushes the page around; a treemap packs the whole catalogue
     into whatever box it is given — 17 products today, 60 after the next import — and stays level
     with the donut beside it. */
  const panelHeight = 360;
  /* Chart data for the product ranking, in Recharts (the app's React chart library — the dashboards
     already draw with it). A COLUMN per product: 17 today sit comfortably across the card, and the
     card keeps ONE fixed height whatever the count, so nothing scrolls and nothing is cut off. Past
     MAX_BARS the tail folds into a single honest "Others" column rather than shaving the axis down
     to slivers — the table below is where a specific product is looked up. */
  const chartData = useMemo(() => {
    const head = byGroup.slice(0, maxBars).map((g) => ({ ...g, fill: accentForProductName(g.product) }));
    const tail = byGroup.slice(maxBars);
    if (!tail.length) return head;
    const rest = tail.reduce((t, g) => ({
      costValue: t.costValue + g.costValue, qty: t.qty + g.qty, skuCount: t.skuCount + g.skuCount, sharePct: t.sharePct + g.sharePct,
    }), { costValue: 0, qty: 0, skuCount: 0, sharePct: 0 });
    return [...head, {
      key: '__others', product: `Others (${tail.length})`, retailValue: 0,
      costValue: Number(rest.costValue.toFixed(2)), qty: rest.qty, skuCount: rest.skuCount,
      sharePct: Number(rest.sharePct.toFixed(2)), fill: token.colorTextTertiary,
    }];
  }, [byGroup, maxBars, token]);

  /* Warehouse mix. The ECharts ring that was here paged its legend (‹ 1/4 ›) — seven sites could
     not fit one line, so four of them were behind an arrow, and the three sub-1% sites drew leader
     lines to labels reading "0%". Now: a Recharts ring with NO labels on the slices, and the legend
     as ordinary HTML underneath that WRAPS. Every warehouse is on screen, with its value, always. */
  const whMix = useMemo(() => {
    const colors = seriesColorsFor(byWarehouse.map((w) => w.warehouse));
    return byWarehouse.map((w) => ({
      ...w,
      // "Bengaluru Warehouse (WH-BLR)" → "WH-BLR": the code is the half a warehouse is known by, and
      // the full name is one hover away.
      code: /\(([^)]+)\)\s*$/.exec(w.warehouse)?.[1] ?? w.warehouse,
      fill: colors[w.warehouse],
    }));
  }, [byWarehouse]);

  const shareCell = (v: number) => (
    <div style={{ minWidth: 96 }}>
      <Progress percent={v} size="small" strokeColor={brand.primary} format={(p) => `${p}%`} />
    </div>
  );

  // Inline batch-number chips shown under a product/variant in the table row.
  const batchChips = (nos: string[]) => (nos.length ? (
    <div style={{ marginTop: 3, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {nos.map((b) => <Tag key={b} style={{ margin: 0, fontFamily: 'monospace', fontSize: 11, lineHeight: '18px' }}>{b}</Tag>)}
    </div>
  ) : null);

  const productCols: DataTableColumn<ProductRow>[] = [
    {
      key: 'product', title: 'Product', dataIndex: 'product', alwaysVisible: true,
      render: (v, r) => (<div><div style={{ fontWeight: 500 }}>{v}</div>{r.sku && !isRedundantSku(r.sku, v) && <div style={{ fontSize: 12, color: token.colorTextTertiary }}>{r.sku}</div>}{batchChips(batchNosByProduct.get(r.productId) ?? [])}</div>),
    },
    { key: 'qty', title: 'On hand', dataIndex: 'qty', align: 'right', sortable: true, render: (v, r) => fmtPackQty(v, r.packSize, r.uom ?? undefined) },
    { key: 'avgUnitCost', title: 'Avg unit price', dataIndex: 'avgUnitCost', align: 'right', sortable: true, render: (v) => inr2(v) },
    { key: 'costValue', title: 'Value (at dealer price)', dataIndex: 'costValue', align: 'right', sortable: true, alwaysVisible: true, render: (v) => <b style={{ color: brand.primaryDark }}>{inr2(v)}</b> },
    { key: 'retailValue', title: 'Value (at MRP)', dataIndex: 'retailValue', align: 'right', sortable: true, render: (v) => inr2(v) },
    { key: 'marginPct', title: 'Margin %', dataIndex: 'marginPct', align: 'right', sortable: true, render: (v: number) => <span style={{ color: marginColor(v), fontWeight: 500 }}>{v}%</span> },
    { key: 'sharePct', title: 'Share of value', dataIndex: 'sharePct', align: 'left', width: 150, sortable: true, render: (v: number) => shareCell(v) },
    { key: 'warehouseCount', title: 'WH', dataIndex: 'warehouseCount', align: 'right', render: (v) => <Tag>{v}</Tag> },
    { key: 'batchCount', title: 'Batches', dataIndex: 'batchCount', align: 'right' },
  ];

  const detailCols: DataTableColumn<ItemRow>[] = [
    {
      key: 'product', title: 'Product', dataIndex: 'product', alwaysVisible: true,
      render: (v, r) => (<div><div style={{ fontWeight: 500 }}>{v}</div>{r.sku && !isRedundantSku(r.sku, v) && <div style={{ fontSize: 12, color: token.colorTextTertiary }}>{r.sku}</div>}{batchChips((r.batches ?? []).map((b) => b.batchNo))}</div>),
    },
    { key: 'warehouse', title: 'Warehouse', dataIndex: 'warehouse', alwaysVisible: true, render: (v) => <Tag tone="blue">{v}</Tag> },
    { key: 'qty', title: 'On hand', dataIndex: 'qty', align: 'right', sortable: true, render: (v, r) => fmtPackQty(v, r.packSize, r.uom ?? undefined) },
    { key: 'avgUnitCost', title: 'Avg unit price', dataIndex: 'avgUnitCost', align: 'right', sortable: true, render: (v) => inr2(v) },
    { key: 'costValue', title: 'Value (at dealer price)', dataIndex: 'costValue', align: 'right', sortable: true, alwaysVisible: true, render: (v) => <b style={{ color: brand.primaryDark }}>{inr2(v)}</b> },
    { key: 'retailValue', title: 'Value (at MRP)', dataIndex: 'retailValue', align: 'right', sortable: true, render: (v) => inr2(v) },
    { key: 'marginPct', title: 'Margin %', dataIndex: 'marginPct', align: 'right', sortable: true, render: (v: number) => <span style={{ color: marginColor(v), fontWeight: 500 }}>{v}%</span> },
    { key: 'sharePct', title: 'Share of value', dataIndex: 'sharePct', align: 'left', width: 150, sortable: true, render: (v: number) => shareCell(v) },
    { key: 'batchCount', title: 'Batches', dataIndex: 'batchCount', align: 'right' },
  ];

  const productExpand = {
    rowExpandable: (r: ProductRow) => items.some((it) => it.productId === r.productId),
    expandedRowRender: (r: ProductRow) => (
      <Table<ItemRow>
        size="small" pagination={false} rowKey="warehouseId"
        dataSource={items.filter((it) => it.productId === r.productId)}
        columns={[
          { title: 'Warehouse', dataIndex: 'warehouse', render: (v) => <Tag tone="blue">{v}</Tag> },
          { title: 'On hand', dataIndex: 'qty', align: 'right' as const, render: (v: number, it: ItemRow) => fmtPackQty(v, it.packSize, it.uom ?? undefined) },
          { title: 'Avg unit price', dataIndex: 'avgUnitCost', align: 'right' as const, render: (v: number) => inr2(v) },
          { title: 'Value (at dealer price)', dataIndex: 'costValue', align: 'right' as const, render: (v: number) => <b>{inr2(v)}</b> },
          { title: 'Value (at MRP)', dataIndex: 'retailValue', align: 'right' as const, render: (v: number) => inr2(v) },
          { title: 'Margin %', dataIndex: 'marginPct', align: 'right' as const, render: (v: number) => (typeof v === 'number' ? <span style={{ color: marginColor(v), fontWeight: 500 }}>{v}%</span> : '—') },
          { title: 'Batches', dataIndex: 'batchCount', align: 'right' as const },
        ]}
      />
    ),
  };

  const detailExpand = {
    rowExpandable: (r: ItemRow) => (r.batches?.length ?? 0) > 0,
    expandedRowRender: (r: ItemRow) => (
      <Table<BatchRow>
        size="small" pagination={false} rowKey="batchNo" dataSource={r.batches}
        columns={[
          { title: 'Batch', dataIndex: 'batchNo' },
          { title: 'Mfg', dataIndex: 'mfgDate', render: (v: string | null) => d10(v) },
          { title: 'Exp', dataIndex: 'expDate', render: (v: string | null) => d10(v) },
          { title: 'On hand', dataIndex: 'qty', align: 'right' as const, render: (v: number) => fmtQty(v) },
          { title: 'Unit price', dataIndex: 'unitCost', align: 'right' as const, render: (v: number) => inr2(v) },
          { title: 'Value (at dealer price)', dataIndex: 'costValue', align: 'right' as const, render: (v: number) => <b>{inr2(v)}</b> },
        ]}
      />
    ),
  };

  const filterBar = (
    <Space wrap>
      <Input prefix={<Search className="size-4" />} placeholder="Product / SKU" aria-label="Product / SKU" className="w-50" value={search} onChange={(e) => setSearch(e.target.value)} />
      {/* Hidden when the viewer has no warehouse access — an always-empty picker reads as broken. */}
      {canSeeWarehouses && (
        <Select
          showSearch optionFilterProp="searchLabel" placeholder="All warehouses" className="w-55"
          value={warehouseId ?? ''} onChange={(v) => setWarehouseId(v || undefined)}
          options={[
            { value: '', label: 'All warehouses', searchLabel: 'All warehouses' },
            ...(warehouses ?? []).map((w: any) => ({ value: String(w.id), ...warehouseOption(w) })),
          ]}
        />
      )}
    </Space>
  );

  return (
    <div>
      {!embedded && !summaryOnly && (
        <>
          <Typography.Title level={4} style={{ marginBottom: 2 }}>Stock Valuation</Typography.Title>
          <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
            On‑hand quantity × dealer price, with retail value at MRP. Available stock only.
          </Typography.Paragraph>
        </>
      )}

      {/* Export sits with the KPI band, not in the table toolbar: valuation is only ever reached as the
          summary block on the Stock page, where that toolbar isn't rendered. */}
      {canExport && (
        <div className="mb-3 flex items-center gap-2">
          <span className="text-sm text-muted">On-hand value at dealer price</span>
          <Button size="sm" className="ml-auto" icon={<Download size={16} />} onClick={() => setExportOpen(true)}>Export</Button>
        </div>
      )}

      {/* KPI cards — the shared ui/StatCard, not antd-compat Card + Statistic. The old band was five
          white boxes whose only colour was an inline hex on the figure itself, which is the one
          place the type scale says colour should NOT go (colour carries hierarchy, weight carries
          emphasis). StatCard moves the colour to where it belongs — a tone wash and an icon chip —
          and brings the band in line with every other register. Same numbers, same formatters. */}
      <div className="mb-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <StatCard label="Stock value (at dealer price)" value={inr(s?.totalCostValue ?? 0)} tone="brand" icon={<IndianRupee className="size-4" />} />
        <StatCard label="Retail value (at MRP)" value={inr(s?.totalRetailValue ?? 0)} tone="gold" icon={<Tags className="size-4" />} />
        <StatCard label="Potential margin" value={inr(s?.totalMargin ?? 0)} hint={s ? `${s.marginPct}%` : undefined} tone="success" icon={<TrendingUp className="size-4" />} />
        <StatCard label="On‑hand units" value={fmtQty(s?.totalUnits ?? 0)} tone="sky" icon={<Boxes className="size-4" />} />
        <StatCard
          label="In stock"
          value={`${s?.distinctProducts ?? 0} SKUs`}
          hint={`${s?.distinctBatches ?? 0} batches · ${s?.distinctWarehouses ?? 0} warehouses`}
          tone="neutral"
          icon={<Layers className="size-4" />}
        />
      </div>

      {/* Breakdown charts. With one warehouse the donut beside this would be the SAME families in
          the same accents adding up to the same total, so the treemap takes the full width instead —
          a second shape saying nothing new is worse than empty space. */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={showWarehouseDonut ? 15 : 24}>
          <Card title={`Stock value by product${byGroup.length ? ` (${byGroup.length})` : ''}`}>
            {empty ? <EmptyState title="No stock to value" /> : (
              <ResponsiveContainer width="100%" height={panelHeight}>
                <BarChart data={chartData} margin={{ top: 20, right: 8, left: 0, bottom: 4 }} barCategoryGap="22%">
                  {/* Each column is filled with its OWN product accent, fading down the bar — the same
                      hue that product wears on the public site, so the colour carries the identity. */}
                  <defs>
                    {chartData.map((d, i) => (
                      <linearGradient key={d.key} id={`sv-bar-${i}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={d.fill} stopOpacity={0.95} />
                        <stop offset="100%" stopColor={d.fill} stopOpacity={0.55} />
                      </linearGradient>
                    ))}
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--gb-border)" />
                  <XAxis dataKey="product" interval={0} height={narrow ? 88 : 78} tickLine={false} axisLine={false}
                    tick={<ProductTick cut={narrow ? 11 : 14} />} />
                  <YAxis width={58} tickLine={false} axisLine={false} tickFormatter={(v: number) => inrShort(v)}
                    tick={{ fontSize: 11, fill: 'var(--gb-text-muted)' }} />
                  <RTooltip cursor={{ fill: 'var(--gb-surface-2)' }} content={<ValueTooltip />} />
                  <Bar dataKey="costValue" radius={[6, 6, 0, 0]} maxBarSize={46} isAnimationActive={false}>
                    {chartData.map((d, i) => <Cell key={d.key} fill={`url(#sv-bar-${i})`} />)}
                    {/* On a phone the columns sit too close for a figure over each one — it becomes a
                        smear of overlapping text. The axis and the tooltip carry the number there. */}
                    {!narrow && <LabelList dataKey="costValue" position="top" formatter={(v: any) => inrShort(v)}
                      style={{ fontSize: 10, fill: 'var(--gb-text-muted)' }} />}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>
        {showWarehouseDonut && (
          <Col xs={24} lg={9}>
            <Card title="Value by warehouse">
              {empty ? <EmptyState title="No stock to value" /> : (
                <div style={{ height: panelHeight }} className="flex flex-col">
                  <div className="min-h-0 flex-1">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={whMix} dataKey="costValue" nameKey="warehouse" innerRadius="52%" outerRadius="78%"
                          paddingAngle={2} stroke="var(--gb-surface)" strokeWidth={3} isAnimationActive={false}>
                          {whMix.map((w) => <Cell key={w.warehouseId} fill={w.fill} />)}
                        </Pie>
                        <RTooltip content={<WarehouseTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  {/* Wraps to as many rows as it needs — no pager, nothing hidden behind an arrow. */}
                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
                    {whMix.map((w) => (
                      <span key={w.warehouseId} className="flex items-center gap-1.5 text-2xs text-muted" title={w.warehouse}>
                        <span className="inline-block size-2 rounded-full" style={{ background: w.fill }} />
                        <span className="tabular-nums text-content">{w.code}</span>
                        <span className="tabular-nums">{inrShort(w.costValue)} · {w.sharePct}%</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          </Col>
        )}
      </Row>

      {/* View toggle — moved out of the table toolbar into a header row above the filters. */}
      {!summaryOnly && (
        <div className="mb-3 flex flex-wrap items-center justify-end gap-2">
          <Segmented value={view} onChange={(v) => setView(v as any)} options={[{ label: 'By product', value: 'product' }, { label: 'Detailed', value: 'detail' }]} />
        </div>
      )}

      {/* Detailed table — hidden in summary-only (merged) mode; the Live Stock table carries the rows. */}
      {!summaryOnly && (
      <Card>
        {view === 'product' ? (
          <DataTable<ProductRow>
            key="product"
            storageKey="valuation-by-product"
            rowKey="productId"
            loading={isFetching}
            dataSource={byProduct}
            columns={productCols}
            expandable={productExpand}
            pagination={{ pageSize: 25, showSizeChanger: true, showTotal: (t) => `${t} products` }}
            toolbarLeft={filterBar}
          />
        ) : (
          <DataTable<ItemRow>
            key="detail"
            storageKey="valuation-detail"
            rowKey={(r) => `${r.warehouseId}-${r.productId}`}
            loading={isFetching}
            dataSource={items}
            columns={detailCols}
            expandable={detailExpand}
            pagination={{ pageSize: 25, showSizeChanger: true, showTotal: (t) => `${t} rows` }}
            toolbarLeft={filterBar}
          />
        )}
      </Card>
      )}
    {/* No period picker: valuation is an "as on now" snapshot of what is physically on hand — a date
        range would promise a historical position the query cannot produce. Page filters carry through. */}
      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title="Export stock valuation"
        description="Excel comes tab-wise: by warehouse, by product, and the batch-level detail a stock-take is reconciled against."
        baseUrl="/stock/valuation/export"
        baseParams={params}
        hidePeriod
      />
    </div>
  );
}
